import { useState, useEffect, useCallback } from "react";

/**
 * Options for the useDropZone hook
 */
interface UseDropZoneOptions {
  onFilesDropped?: (files: File[]) => void | Promise<void>;
  detectMediaSet?: boolean;
  disabled?: boolean;
  acceptedFileTypes?: string[]; // Array of MIME types, e.g., ["image/png", "image/jpeg", "video/mp4"]
}

/**
 * Hook for managing drag and drop state and handlers for a drop zone
 */
export function useDropZone(options?: UseDropZoneOptions) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isDragOverMediaSet, setIsDragOverMediaSet] = useState(false);

  // Global reset listeners (consolidates the duplicated useEffects)
  useEffect(() => {
    const handleGlobalEnd = () => {
      setIsDragOver(false);
      setIsDragOverMediaSet(false);
    };

    // Use capture phase to catch events before they're handled
    document.addEventListener("dragend", handleGlobalEnd, true);
    document.addEventListener("drop", handleGlobalEnd, true);

    // Optional media set detection
    let handleDragOver: ((e: DragEvent) => void) | undefined;
    if (options?.detectMediaSet) {
      handleDragOver = (e: DragEvent) => {
        const target = e.target as HTMLElement;
        const isOverMediaSet = !!target.closest('[data-drop-zone="media-set-collection"]');
        setIsDragOverMediaSet(isOverMediaSet);
      };
      document.addEventListener("dragover", handleDragOver, true);
    }

    return () => {
      document.removeEventListener("dragend", handleGlobalEnd, true);
      document.removeEventListener("drop", handleGlobalEnd, true);
      if (handleDragOver) {
        document.removeEventListener("dragover", handleDragOver, true);
      }
    };
  }, [options?.detectMediaSet]);

  // Local handlers
  const onDragOver = useCallback(
    (e: React.DragEvent) => {
      if (e.dataTransfer.types.includes("Files")) {
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = options?.disabled ? "none" : "copy";
        setIsDragOver(true);
      }
    },
    [options?.disabled],
  );

  const onDragLeave = useCallback((e: React.DragEvent) => {
    // Only set to false if we're leaving the container entirely
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;

    if (x < rect.left || x >= rect.right || y < rect.top || y >= rect.bottom) {
      setIsDragOver(false);
    }
  }, []);

  const onDrop = useCallback(
    async (e: React.DragEvent) => {
      // Only file drops belong to this zone; let TipTap and media-tile drags
      // finish their own drops without this listener swallowing them.
      if (!e.dataTransfer.types.includes("Files")) return;

      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      // Don't process drops if disabled
      if (options?.disabled) {
        return;
      }

      let files = Array.from(e.dataTransfer.files);

      // Filter by accepted file types if specified
      if (options?.acceptedFileTypes && options.acceptedFileTypes.length > 0) {
        files = files.filter((file) => options.acceptedFileTypes!.includes(file.type));
      }

      if (files.length > 0 && options?.onFilesDropped) {
        await options.onFilesDropped(files);
      }
    },
    [options],
  );

  return {
    isDragOver,
    isDragOverMediaSet,
    isDisabled: options?.disabled ?? false,
    handlers: {
      onDragOver,
      onDragLeave,
      onDrop,
    },
  };
}

/**
 * Hook for global drag and drop prevention outside of drop zones
 */
export function useGlobalDragDropPrevention() {
  useEffect(() => {
    const preventDefault = (e: DragEvent) => {
      // Only prevent default for file drops, not text or other drag types
      if (e.dataTransfer?.types.includes("Files")) {
        e.preventDefault();
      }
    };

    const handleDrop = (e: DragEvent) => {
      // Prevent browser from opening the file
      if (e.dataTransfer?.types.includes("Files")) {
        // Check if the drop is happening in a valid drop zone
        const target = e.target as HTMLElement;
        const isInDropZone = target.closest("[data-drop-zone]");

        // Only prevent default and stop propagation if NOT in valid drop areas
        if (!isInDropZone) {
          e.preventDefault();
          e.stopPropagation();
        } else {
          // Still prevent default to stop browser navigation, but let event propagate
          e.preventDefault();
        }
      }
    };

    // Add listeners to document to catch all drag events
    document.addEventListener("dragover", preventDefault);
    document.addEventListener("drop", handleDrop);

    // Cleanup
    return () => {
      document.removeEventListener("dragover", preventDefault);
      document.removeEventListener("drop", handleDrop);
    };
  }, []);
}
